import { useState } from "react"

import { useFormSections, useFields } from "web/hooks/data"

import { useCurrentTLO } from "web/hooks/context"

import { ContentBox, Toggle } from "web/components"

import ViewOrEdit from "./ViewOrEdit"

function getFieldsWithStageStatus(fields, missingStageFields) {
  if (!missingStageFields) {
    missingStageFields = []
  }

  return fields.map((field) => {
    const matchingStageField = missingStageFields.find(
      (msf) => msf.field.id === field.id
    )

    const isMissingField = matchingStageField !== undefined

    return {
      ...field,
      isOptionalRequired: isMissingField && !matchingStageField.mandatory,
      isMandatoryRequired: isMissingField && matchingStageField.mandatory,
    }
  })
}

export default function ViewOrEditDealAsset({
  excludedFieldIds = [],
  supportsMissingStageFields = false,
}) {
  const [fieldFilter, setFieldFilter] = useState("")
  const [showAllFields, setShowAllFields] = useState({ value: false })

  const { data, userCan } = useCurrentTLO()
  const assetFields = useFields({ subjectType: "asset" })

  // FIXME: This is almost certainly wrong and should only be the DealAsset fields, as we work with the asset fields separately, but this is being introduced in a change to remove GlobalDataContext, so not going to fix it now in case something else depends on it being wrong.
  const dealAssetFields = useFields({
    subjectType: "deal_asset",
    includeMultipleSubjectsFields: true,
  })

  const assetFormSections = useFormSections({ subjectType: "asset" })
  const dealAssetFormSections = useFormSections({ subjectType: "dealAsset" })

  const userCanUpdate = userCan("update")

  const missingStageFields = supportsMissingStageFields
    ? data.missing_stage_fields
    : []

  const missingAssetFields = []
  const missingDealAssetFields = []

  missingStageFields.forEach((stageField) => {
    if (stageField.field.subject_type === "Asset") {
      missingAssetFields.push(stageField)
    } else if (stageField.field.subject_type === "DealAsset") {
      missingDealAssetFields.push(stageField)
    }
  })

  async function handleFieldChange(update) {
    const valueBlob = (x) => ({ value: x, form_value: x })

    const formattedUpdate = Object.keys(update).reduce(
      (result, key) => ({ ...result, [key]: valueBlob(update[key]) }),
      {}
    )

    // newLocalData is used to local mutate swr and avoid
    // API call roundtrip slowing down update in UI
    const newLocalData = {
      ...data,
      values: { ...data.values, ...formattedUpdate },
    }
    data.update(update, newLocalData)
  }

  function handleLoadEmpty() {
    if (userCanUpdate) setShowAllFields({ value: true })
  }

  const editableAssetFields = assetFields.filter(
    (f) => !excludedFieldIds.includes(f.id)
  )

  const decoratedAssetFields = getFieldsWithStageStatus(
    editableAssetFields,
    missingAssetFields
  )

  const editableDealAssetFields = dealAssetFields.filter(
    (f) => !excludedFieldIds.includes(f.id)
  )

  const decoratedDealAssetFields = getFieldsWithStageStatus(
    editableDealAssetFields,
    missingDealAssetFields
  )

  const hasDealAssetFields = Object.keys(data.values).some((field) =>
    dealAssetFields.map((f) => f.key).includes(field)
  )

  const hasDealAssetFieldValues =
    Object.keys(data.values).filter(
      (f) => f !== "deal_asset_origin_id" && f.match(/deal_asset/gi)
    ).length > 0

  return (
    <>
      <div className="view-or-edit-tlo-tools">
        {userCanUpdate && (
          <div className="view-or-edit-show-all-fields">
            <Toggle
              label="Show All Fields"
              checked={showAllFields.value}
              onClick={() => setShowAllFields({ value: !showAllFields.value })}
            />
          </div>
        )}
        <div className="view-or-edit-quick-filter">
          <input
            value={fieldFilter}
            placeholder="Search fields..."
            onChange={(e) => setFieldFilter(e.target.value)}
          />
        </div>
      </div>
      {((hasDealAssetFields && hasDealAssetFieldValues) ||
        missingDealAssetFields.length > 0 ||
        showAllFields.value) && (
        <ContentBox className="mb-1">
          <span className="-mt-1 mb-5 flex items-center border-b pb-2">
            <span className="text-lg font-bold">Deal-Related Details</span>
          </span>

          <ViewOrEdit
            missingStageFields={missingDealAssetFields}
            supportsMissingStageFields={supportsMissingStageFields}
            data={data}
            fields={decoratedDealAssetFields}
            fieldFilter={fieldFilter}
            sections={dealAssetFormSections}
            showAll={showAllFields.value}
            readOnly={!userCanUpdate}
            onFieldChanged={handleFieldChange}
            onLoadedEmpty={handleLoadEmpty}
          />
        </ContentBox>
      )}
      <ContentBox>
        <span className="-mt-1 mb-5 flex items-center border-b pb-2">
          <span className="text-lg font-bold">Asset Details</span>
        </span>
        <ViewOrEdit
          missingStageFields={missingAssetFields}
          supportsMissingStageFields={supportsMissingStageFields}
          data={data}
          fields={decoratedAssetFields}
          fieldFilter={fieldFilter}
          sections={assetFormSections}
          showAll={showAllFields.value}
          readOnly={!userCanUpdate}
          onFieldChanged={handleFieldChange}
          onLoadedEmpty={handleLoadEmpty}
        />
      </ContentBox>
    </>
  )
}
